import type { ApiPlugin } from '@srm/core/http';

const supportPreviewPathPattern = /(?:^|\/)support\/document-preview\/([^/?#]+)(?:\/|$)/i;

const isAbsoluteOrProtocolRelativeUrl = (value: string): boolean => {
    if (value.startsWith('//')) return true;

    const schemeSeparator = value.indexOf(':');
    if (schemeSeparator <= 0) return false;
    const firstPathSeparator = [value.indexOf('/'), value.indexOf('?'), value.indexOf('#')]
        .filter((index) => index >= 0)
        .reduce((first, index) => Math.min(first, index), Number.POSITIVE_INFINITY);
    return schemeSeparator < firstPathSeparator;
};

export const SUPPORT_PREVIEW_REQUEST_BLOCKED_MESSAGE =
    'Обычный API-запрос заблокирован: support preview принимает только session-scoped endpoints.';

export const isSupportPreviewBrowserRoute = (pathname?: string): boolean =>
    !!pathname && supportPreviewPathPattern.test(pathname);

const getSupportPreviewSessionId = (value?: string): string | undefined => {
    const encodedSessionId = value?.match(supportPreviewPathPattern)?.[1];
    if (!encodedSessionId) return undefined;

    try {
        return decodeURIComponent(encodedSessionId);
    } catch {
        return undefined;
    }
};

export const isSupportPreviewSessionRequest = (
    requestUrl?: string,
    baseUrl?: string,
    browserOrigin?: string,
    expectedSessionId?: string,
): boolean => {
    if (!requestUrl) return false;

    let requestPath = requestUrl.split(/[?#]/, 1)[0];
    if (isAbsoluteOrProtocolRelativeUrl(requestUrl)) {
        try {
            if (!browserOrigin) return false;
            const request = new URL(requestUrl, browserOrigin);
            const apiOrigin = baseUrl ? new URL(baseUrl, browserOrigin).origin : browserOrigin;
            if (request.origin !== apiOrigin) return false;
            requestPath = request.pathname;
        } catch {
            return false;
        }
    }

    const requestSessionId = getSupportPreviewSessionId(requestPath);
    return !!requestSessionId && (!expectedSessionId || requestSessionId === expectedSessionId);
};

interface ISupportPreviewRequestGuardInput {
    browserPathname?: string;
    browserOrigin?: string;
    requestUrl?: string;
    baseUrl?: string;
}

export const shouldBlockSupportPreviewRequest = ({
    browserPathname,
    browserOrigin,
    requestUrl,
    baseUrl,
}: ISupportPreviewRequestGuardInput): boolean => {
    const browserSessionId = getSupportPreviewSessionId(browserPathname);
    return (
        !!browserSessionId &&
        !isSupportPreviewSessionRequest(requestUrl, baseUrl, browserOrigin, browserSessionId)
    );
};

/** Плагин клиента: в предпросмотре поддержки обычные запросы (и keepalive) не уходят — только запросы своего сеанса. */
export const supportPreviewGuardPlugin = (): ApiPlugin => ({
    name: 'support-preview-guard',
    request: ({ url, baseURL }) =>
        shouldBlockSupportPreviewRequest({
            browserPathname: typeof window === 'undefined' ? undefined : window.location.pathname,
            browserOrigin: typeof window === 'undefined' ? undefined : window.location.origin,
            requestUrl: url,
            baseUrl: baseURL,
        })
            ? SUPPORT_PREVIEW_REQUEST_BLOCKED_MESSAGE
            : undefined,
});
